import { useState, useEffect } from 'react';
import { Calendar, CheckCircle2, ArrowRight, UserCheck, Shield, Award, MessageSquare, ChevronLeft, ChevronRight, Phone } from 'lucide-react';
import { fetchCollection } from '../../lib/cms';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function DemoDe() {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    async function loadData() {
      const testData = await fetchCollection('testimonials', 'de');
      setTestimonials(testData);
    }
    loadData();
  }, []);

  useEffect(() => {
    if (testimonials.length === 0 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials, isPaused]);

  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const fordele = [
    {
      title: 'Praxistest vor Ort',
      desc: 'Sie dürfen die Maschine in Ihrem eigenen Stall in Ihren Boxen und auf Ihren Reitplätzen ausprobieren.'
    },
    {
      title: 'Persönliche Beratung',
      desc: 'Rasmus führt Ihnen die Maschine persönlich vor und berät Sie zum optimalen Einsatz bei Ihren Gegebenheiten.'
    },
    {
      title: '100% Unverbindlich',
      desc: 'Es kostet nichts und es gibt keinen Kaufdruck. Wir möchten Ihnen schlicht den echten Mehrwert zeigen.'
    }
  ];

  const trin = [
    {
      step: '1',
      title: 'Termin anfragen',
      desc: (
        <span>
          Füllen Sie das Formular oben aus oder rufen Sie Rasmus direkt an unter{' '}
          <a href="tel:25303589" className="text-[#0E67A2] hover:underline font-bold">
            +45 2530 3589
          </a>.
        </span>
      )
    },
    {
      step: '2',
      title: 'Wir kommen zu Ihnen',
      desc: 'Rasmus kommt mit der Vorführmaschine und Zubehör direkt zu Ihrer Reitanlage oder Ihrem Stall.'
    },
    {
      step: '3',
      title: 'Selbst ausprobieren',
      desc: 'Sie und Ihre Stallkräfte testen die Maschine selbst und spüren den Unterschied im Rücken sofort.'
    }
  ];

  const demoSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Kostenlose Demo des Danshakers',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Danshaker',
    },
    description: 'Buchen Sie eine unverbindliche und kostenlose Vorführung des Danshakers direkt in Ihrem eigenen Stall.',
    areaServed: 'DK, DE',
    isOfferCatalogOf: {
      '@type': 'OfferCatalog',
      name: 'Demonstration im eigenen Stall',
    },
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-950 antialiased">
      <SEO
        title="Kostenlose Demo buchen - Testen Sie den Danshaker im eigenen Stall"
        description="Buchen Sie eine kostenlose und unverbindliche Vorführung des Danshakers direkt in Ihrem eigenen Pferdestall. Probieren Sie die Maschine vor dem Kauf aus."
        path="/de/kostenlose-demo"
        schema={demoSchema}
        lang="de"
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.08),transparent_60%)]" />

      {/* Hero Section - Booking Form is the very first content */}
      <section className="mx-auto max-w-7xl px-4 pt-12 pb-16 md:px-8 md:pt-16">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          {/* Left Column: Info */}
          <div className="space-y-6 lg:col-span-6 order-2 lg:order-1 lg:sticky lg:top-40">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#0E67A2]/30 bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-black text-[#0E67A2]">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Kostenlos und unverbindlich</span>
            </div>
            <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl md:text-6xl leading-tight">
              Erleben Sie den Danshaker<br />
              <span className="bg-gradient-to-r from-[#0E67A2] to-sky-700 bg-clip-text text-transparent">im eigenen Stall</span>
            </h1>
            <p className="max-w-xl text-lg text-slate-900 font-semibold leading-relaxed">
              Wir besuchen Sie vor Ort und führen den Danshaker direkt in Ihrem Stall vor. Sie können die Maschine selbst in Ihren Pferdeboxen testen – vollkommen unverbindlich.
            </p>
            <p className="max-w-xl text-base text-slate-800 font-medium leading-relaxed">
              Dies ist derzeit nur in Dänemark möglich.
            </p>
            
            <div className="rounded-xl border-2 border-[#0E67A2]/30 bg-white p-5 max-w-xl shadow-md">
              <p className="text-sm text-slate-950 font-bold">
                Möchten Sie die Maschine über einen längeren Zeitraum vor dem Kauf testen?
              </p>
              <p className="mt-2 text-base text-[#0E67A2] font-black">
                Mieten Sie die Testmaschine 14 Tage lang für nur 135 € zzgl. MwSt. + Kaution.
              </p>
            </div>

            {/* Quick benefits list in hero */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <h2 className="text-xs font-black text-slate-950 uppercase tracking-wider">Warum eine Vorführung buchen?</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>100% unverbindlich</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>In eigenen Boxen testen</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>Fachkundige Anleitung</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>Keinerlei Kaufzwang</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Booking Form */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="rounded-3xl border-2 border-[#0E67A2] bg-white p-6 md:p-8 shadow-2xl relative">
              <div className="absolute -top-3.5 left-8 bg-[#0E67A2] text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-wider">
                Demo buchen
              </div>
              <h2 className="text-2xl font-black text-slate-950 mb-2">Formular ausfüllen</h2>
              <p className="text-sm text-slate-800 font-semibold mb-6">
                Wir melden uns schnellstmöglich bei Ihnen, um einen passenden Termin zu vereinbaren.
              </p>
              <ContactForm defaultService="Gratis demo" buttonText="Kostenlose Demo anfordern" lang="de" />
            </div>
          </div>
        </div>
      </section>

      {/* Process / How it works */}
      <section className="border-t-2 border-slate-300 bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-black text-slate-950 md:text-4xl">So einfach läuft die Vorführung ab</h2>
            <p className="mt-4 text-slate-900 font-bold">In 3 einfachen Schritten zur Live-Demo bei Ihnen vor Ort.</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {trin.map((item) => (
              <div key={item.step} className="relative rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 shadow-md">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-[#0E67A2] text-xl font-black text-white">
                  {item.step}
                </div>
                <h3 className="text-xl font-black text-slate-950 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-900 font-semibold leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Detailed */}
      <section className="py-16 mx-auto max-w-7xl px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl font-black text-slate-950 md:text-4xl">Ihre Vorteile bei einer Live-Vorführung</h2>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {fordele.map((item, idx) => (
            <div key={idx} className="rounded-2xl border-2 border-slate-300 bg-white p-8 shadow-md">
              <CheckCircle2 className="h-10 w-10 text-[#0E67A2] mb-4" aria-hidden="true" />
              <h3 className="text-xl font-black text-slate-950 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="border-t-2 border-slate-300 bg-white py-16">
          <div className="mx-auto max-w-5xl px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-slate-950">Was Kunden über die Vorführung sagen</h2>
            </div>

            <div className="relative overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonials.map((item) => (
                  <div key={item.id} className="w-full shrink-0 px-4">
                    <div className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 shadow-md text-center max-w-2xl mx-auto">
                      <div className="flex justify-center gap-1 text-amber-400 mb-4" aria-label="5 von 5 Sternen">
                        <i className="fa-solid fa-star" />
                        <i className="fa-solid fa-star" />
                        <i className="fa-solid fa-star" />
                        <i className="fa-solid fa-star" />
                        <i className="fa-solid fa-star" />
                      </div>
                      <div
                        className="text-base text-slate-950 font-bold italic leading-relaxed mb-6"
                        dangerouslySetInnerHTML={{ __html: item.text }}
                      />
                      <div>
                        <p className="font-black text-slate-950">{item.name}</p>
                        <p className="text-xs text-slate-800 font-bold">{item.company}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {testimonials.length > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition cursor-pointer"
                  aria-label="Vorherige Bewertung"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="flex gap-1.5">
                  {testimonials.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-2 rounded-full transition-all cursor-pointer ${
                        idx === currentIndex ? 'w-6 bg-[#0E67A2]' : 'w-2 bg-slate-300'
                      }`}
                      aria-label={`Gehe zu Bewertung ${idx + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={nextSlide}
                  className="p-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition cursor-pointer"
                  aria-label="Nächste Bewertung"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Bottom CTA / Reassurance */}
      <section className="py-16 mx-auto max-w-4xl px-4 text-center">
        <div className="rounded-3xl bg-slate-900 p-8 md:p-12 text-white shadow-xl space-y-6">
          <h2 className="text-2xl md:text-3xl font-black">Haben Sie vorab Fragen zur Vorführung?</h2>
          <p className="text-slate-300 text-sm md:text-base font-medium max-w-xl mx-auto">
            Rufen Sie uns direkt an – wir beraten Sie gern unverbindlich zu Boxengrößen, Einstreuarten und Praxiserfahrungen.
          </p>
          <div className="pt-2">
            <a
              href="tel:25303589"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0E67A2] px-8 py-4 font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-md text-base"
            >
              <Phone className="h-5 w-5" />
              +45 2530 3589 anrufen
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
